import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  Platform,
} from 'react-native';
import { Business } from '../types/business';
import AppointmentRequestForm from '../components/AppointmentRequestForm';
import TransportationQuoteRequestForm from '../components/TransportationQuoteRequestForm';
import { appointmentRequestService } from '../services/appointmentRequestService';
import { isTransportationBusinessCategory } from '../utils/transportationCategoryUtils';

interface BusinessDetailScreenProps {
  business: Business;
  onBack: () => void;
  onRequestSubmitted?: () => void;
  onBookBusiness?: (business: Business) => void;
}

const BusinessDetailScreen: React.FC<BusinessDetailScreenProps> = ({
  business,
  onBack,
  onRequestSubmitted,
  onBookBusiness,
}) => {
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestType, setRequestType] = useState<'appointment' | 'estimate'>('appointment');
  const [remainingRequests, setRemainingRequests] = useState<number | null>(null);
  const [hasReachedCap, setHasReachedCap] = useState(false);

  useEffect(() => {
    console.log('🔍 BusinessDetailScreen - Business data:', {
      name: business.name,
      isOnBookAPT: business.isOnBookAPT,
      phone: business.phone,
      hasPhone: !!business.phone,
    });
    // Check request count for this business (treat undefined as false - not on BookAPT)
    if (business.isOnBookAPT !== true && business.phone) {
      checkRequestCount();
    }
  }, [business]);

  const checkRequestCount = async () => {
    try {
      const remaining = await appointmentRequestService.getRemainingRequests(business.phone);
      const reachedCap = await appointmentRequestService.hasReachedCap(business.phone);
      setRemainingRequests(remaining);
      setHasReachedCap(reachedCap);
    } catch (error) {
      console.error('Error checking request count:', error);
    }
  };

  const handleBookNow = () => {
    console.log('🔍 BusinessDetailScreen - handleRequestAppointment:', {
      businessName: business.name,
      isOnBookAPT: business.isOnBookAPT,
      phone: business.phone,
    });
    if (business.isOnBookAPT === true) {
      if (onBookBusiness) {
        onBookBusiness(business);
      } else {
        Alert.alert('Book Appointment', 'This business is on BookAPT and ready for instant booking.');
      }
    } else {
      Alert.alert('Instant Booking Unavailable', `${business.name} is not on BookAPT yet. Use Request Booking or Request Estimate.`);
    }
  };

  const openRequestForm = (type: 'appointment' | 'estimate') => {
    setRequestType(type);
    setShowRequestForm(true);
  };

  const handleFormSubmitted = () => {
    setShowRequestForm(false);
    // Alert is now handled in AppointmentRequestForm component
    if (onRequestSubmitted) {
      onRequestSubmitted();
    }
  };

  const handleFormCancel = () => {
    setShowRequestForm(false);
  };

  if (showRequestForm) {
    if (requestType === 'estimate' && isTransportationBusinessCategory(business.category)) {
      return (
        <TransportationQuoteRequestForm
          business={business}
          onCancel={handleFormCancel}
          onSubmit={handleFormSubmitted}
        />
      );
    }
    return (
      <AppointmentRequestForm
        business={business}
        requestType={requestType}
        onCancel={handleFormCancel}
        onSubmit={handleFormSubmitted}
      />
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.content}>
        {/* Business Name */}
        <Text style={styles.businessName}>{business.name}</Text>

        {/* Status Badge */}
        {business.isOnBookAPT === true ? (
          <View style={[styles.badge, styles.onPlatformBadge]}>
            <Text style={styles.badgeText}>✅ On BookAPT - Book Instantly!</Text>
          </View>
        ) : (
          <View style={[styles.badge, styles.notOnPlatformBadge]}>
            <Text style={styles.badgeText}>⚠️ Not on BookAPT yet</Text>
          </View>
        )}

        {/* Business Info */}
        <View style={styles.infoSection}>
          {business.address && typeof business.address === 'string' && business.address.trim().length > 0 && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>📍 Address:</Text>
              <Text style={styles.infoValue}>{business.address}</Text>
            </View>
          )}

          {business.phone && typeof business.phone === 'string' && business.phone.trim().length > 0 && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>📞 Phone:</Text>
              <Text style={styles.infoValue}>{business.phone}</Text>
            </View>
          )}

          {business.category && typeof business.category === 'string' && business.category.trim().length > 0 && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>🏷️ Category:</Text>
              <Text style={styles.infoValue}>{business.category}</Text>
            </View>
          )}

          {business.rating !== undefined && business.rating !== null && typeof business.rating === 'number' && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>⭐ Rating:</Text>
              <Text style={styles.infoValue}>{business.rating.toFixed(1)} / 5.0</Text>
            </View>
          )}

          {business.distance && typeof business.distance === 'string' && business.distance.trim().length > 0 && business.distance !== 'N/A' && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>📏 Distance:</Text>
              <Text style={styles.infoValue}>{business.distance}</Text>
            </View>
          )}
        </View>

        {/* Action Section */}
        <View style={styles.actionSection}>
          <TouchableOpacity
            style={[styles.actionButton, styles.bookButton]}
            onPress={handleBookNow}
          >
            <Text style={styles.actionButtonText}>Book Now</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.actionButton, styles.requestButton]}
            onPress={() => openRequestForm('appointment')}
          >
            <Text style={styles.actionButtonText}>Request Booking</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.actionButton, styles.estimateButton]}
            onPress={() => openRequestForm('estimate')}
          >
            <Text style={styles.actionButtonText}>Request Estimate</Text>
          </TouchableOpacity>

          {business.isOnBookAPT !== true && hasReachedCap ? (
            <View style={styles.capReachedBox}>
              <Text style={styles.capReachedTitle}>🚨 Request Limit Reached</Text>
              <Text style={styles.capReachedText}>
                We've already sent multiple requests to {business.name}. They need to join BookAPT to receive more requests automatically.
              </Text>
              <TouchableOpacity
                style={[styles.actionButton, styles.inviteButton]}
                onPress={() => {
                  Alert.alert(
                    'Invite Business',
                    `Would you like to invite ${business.name} to join BookAPT?`,
                    [
                      { text: 'Cancel', style: 'cancel' },
                      {
                        text: 'Send Invitation',
                        onPress: () => {
                          Alert.alert('Invitation', 'Invitation feature coming soon!');
                        }
                      }
                    ]
                  );
                }}
              >
                <Text style={styles.actionButtonText}>📧 Invite to BookAPT</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              {business.isOnBookAPT !== true && remainingRequests !== null && remainingRequests < 3 && (
                <View style={styles.requestCountBox}>
                  <Text style={styles.requestCountText}>
                    ⚠️ {remainingRequests} free request{remainingRequests !== 1 ? 's' : ''} remaining for this business
                  </Text>
                </View>
              )}
              <View style={styles.infoBox}>
                <Text style={styles.infoBoxText}>
                  {business.isOnBookAPT === true
                    ? `Book instantly or request a custom booking/estimate from ${business.name}.`
                    : `We'll contact ${business.name} for booking and estimate requests, and can invite them to join BookAPT.`}
                </Text>
              </View>
            </>
          )}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    padding: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    paddingVertical: 8,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
  },
  content: {
    padding: 16,
  },
  businessName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
  },
  badge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    alignSelf: 'flex-start',
    marginBottom: 16,
  },
  onPlatformBadge: {
    backgroundColor: '#d4edda',
  },
  notOnPlatformBadge: {
    backgroundColor: '#fff3cd',
  },
  badgeText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  infoSection: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginRight: 8,
    minWidth: 80,
  },
  infoValue: {
    fontSize: 14,
    color: '#333',
    flex: 1,
  },
  actionSection: {
    marginTop: 8,
  },
  actionButton: {
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  bookButton: {
    backgroundColor: '#007AFF',
  },
  requestButton: {
    backgroundColor: '#28a745',
  },
  estimateButton: {
    backgroundColor: '#7C3AED',
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  infoBox: {
    backgroundColor: '#e7f3ff',
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#007AFF',
  },
  infoBoxText: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
  },
  requestCountBox: {
    backgroundColor: '#fff3cd',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#ffc107',
  },
  requestCountText: {
    fontSize: 13,
    color: '#856404',
    fontWeight: '600',
  },
  capReachedBox: {
    backgroundColor: '#f8d7da',
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#dc3545',
  },
  capReachedTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#721c24',
    marginBottom: 8,
  },
  capReachedText: {
    fontSize: 14,
    color: '#721c24',
    lineHeight: 20,
    marginBottom: 12,
  },
  inviteButton: {
    backgroundColor: '#007AFF',
  },
});

export default BusinessDetailScreen;

